import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { createAuthContext } from './AuthContext';

const MS_AUTHORITY_DOMAIN = 'https://login.microsoftonline.com'
const INVALID_GRANT_CODE = 'invalid_grant'
const REMEMBERED_UP = "rememberedUserP"

const getHostUrl = () => {
  const port = window.location.hostname === 'localhost' ? `:${window.location.port}` : '';
  return `${[window.location.protocol, '//', window.location.hostname, port].join('')}`;
};

export const createAzureAuthenticator = (context = createAuthContext()) => {
  const config = context
    ? {
        auth: {
          clientId: `${context.azureClientId}`,
          authority: `${MS_AUTHORITY_DOMAIN}/${context.azureTenantId}`,
          redirectUri: getHostUrl(),
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      }
    : undefined;

  const authenticator = {
    context,
    config,

    createPublicClientApplication(customConfig) {
      const configToUse = customConfig ?? this.config;
      this.context.publicClientApplication = new PublicClientApplication(configToUse);
      return this.context.publicClientApplication;
    },

    async lazyInit(customConfig) {
      if (!this.context.publicClientApplication) {
        this.context.publicClientApplication = this.createPublicClientApplication(customConfig);
      }
      if (!this.context.isInitialized) {
        await this.context.publicClientApplication.initialize()
        this.context.isInitialized = true
      }
    },

    getCurrentAccount() {
      const accounts = this.context.publicClientApplication?.getAllAccounts() ?? [];
      const currentAccount = accounts.find(
        (account) => account.tenantId === this.context.azureTenantId
      );
      return currentAccount;
    },

    async acquireTokenSilent(account, publicClientApplication, redirectIfRequired = true) {
      try {
        const authResponse = await publicClientApplication.acquireTokenSilent({
          account,
          scopes: this.context.scopes,
          authority: this.config?.auth.authority,
          forceRefresh: true,
        });

        return authResponse;
      } catch (err) {
        if ((err instanceof InteractionRequiredAuthError || err.errorCode === INVALID_GRANT_CODE) && redirectIfRequired) {
          console.log(
            'InteractionRequiredAuthError: Could not acquire a token, attempting to redirect to login',
            err
          );
          this.acquireTokenRedirect()
        } else {
          console.log('Unhandled Exception: ', err);
          console.log('Ending for now...');
        }
      }
      return undefined
    },

    async acquireTokenRedirect() {
      await this.lazyInit();
      const { publicClientApplication } = this.context;
      publicClientApplication.acquireTokenRedirect({
            scopes: this.context.scopes,
            authority: this.config?.auth.authority,
      })
    },

    async authenticate(hint) {
      await this.lazyInit();
      const { publicClientApplication } = this.context;

      try {
        let tokenResponse = await publicClientApplication?.handleRedirectPromise();
        let account;
        if (tokenResponse) {
          account = tokenResponse.account;
          if (account?.username) {
            localStorage.setItem(REMEMBERED_UP, account.username)
          }
          publicClientApplication?.setActiveAccount(account);
        } else {
          account = this.getCurrentAccount();
        }

        if (account && tokenResponse) {
          const exp = tokenResponse.idTokenClaims?.exp;
          if (exp) {
            const expirationTime = new Date(exp * 1000).getTime();
            const now = new Date().getTime();
            const minutesToExpiration = (expirationTime - now) / 1000 / 60;

            if (minutesToExpiration < 10) {
              return await this.acquireTokenSilent(account, publicClientApplication);
            }
          } else {
            return await this.acquireTokenSilent(account, publicClientApplication);
          }
          return tokenResponse;
        } else if (account) {
          return await this.acquireTokenSilent(account, publicClientApplication);
        } else {
          if (!hint) {
            hint = localStorage.getItem(REMEMBERED_UP)
          }
          if (hint) {
            try {
              return await publicClientApplication.ssoSilent({ 
                scopes: this.context.scopes,
                loginHint: hint 
              })
            } catch (e) {
              publicClientApplication?.loginRedirect({
                scopes: this.context.scopes,
                authority: this.config?.auth.authority,
                loginHint: hint ?? undefined,
              })
            }
          } else {
            try {
              return await publicClientApplication.ssoSilent({ 
                scopes: this.context.scopes
              })
            } catch (e) {
              // NOTHING TO DO HERE. 
            }
            
            // User should either be at the login page, or if any tokening was successful should be logged in.
          }
        }
      } catch (error) {
        console.error('authenticate error', error)
      }
    },

    async ssoSilent(request) {
      await this.lazyInit();
      const { publicClientApplication } = this.context;

      try {
        return await publicClientApplication?.ssoSilent(request);
      } catch (outerError) {
        if (outerError instanceof InteractionRequiredAuthError) {
          publicClientApplication?.loginRedirect(request).catch((innerError) =>
            console.log('Login error:', innerError)
          );
        } else {
          console.log('Login error:', outerError);
        }
      }
    },

    async loginPopup() {
      await this.lazyInit();
      const { publicClientApplication } = this.context;

      publicClientApplication?.addEventCallback((message) => {
        console.info(message);
      });

      publicClientApplication?.loginPopup()
        .then((response) => {
          publicClientApplication?.setActiveAccount(response.account);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    async logoutReturn() {
      await this.lazyInit();
      const { publicClientApplication } = this.context;
      const activeAccount = publicClientApplication?.getActiveAccount();

      return publicClientApplication?.logoutRedirect({
        account: activeAccount,
        logoutHint: activeAccount?.idTokenClaims?.login_hint,
        authority: this.config?.auth.authority,
        postLogoutRedirectUri: `${getHostUrl()}/logout`,
      });
    },

    async logout() {
      await this.lazyInit();
      const { publicClientApplication } = this.context;
      const activeAccount = this.getCurrentAccount();
      localStorage.removeItem(REMEMBERED_UP)

      return publicClientApplication?.logoutRedirect({
        account: activeAccount,
        logoutHint: activeAccount?.idTokenClaims?.login_hint,
        authority: this.config?.auth.authority,
        postLogoutRedirectUri: `${getHostUrl()}`,
      });
    },
  };

  return authenticator;
};